import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// @mui material components
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Icon from '@mui/material/Icon';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';
import MDBadgeDot from 'components/MDBadgeDot';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDAlert from 'components/MDAlert';

// Material Dashboard 2 PRO React TS examples components
import DashboardLayout from 'composites/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'composites/Navbars/DashboardNavbar';
import Footer from 'composites/Footer';
import DefaultStatisticsCard from 'composites/Cards/StatisticsCards/DefaultStatisticsCard';
import DefaultLineChart from 'composites/Charts/LineCharts/DefaultLineChart';
import DataTable from 'composites/Tables/DataTable';

// Data
import defaultLineChartData from './data/defaultLineChartData';
import { useUser } from 'context/user';

// Firebase
import { getAuth } from 'firebase/auth';
import {
  collection,
  doc,
  getDocs,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from '../../firebase.js';
import axios from 'axios';
import { PRICE_PER_TOKEN, FIREBASE_API_URL } from 'config';

interface Device {
  id: string;
  name: string;
  lastActive: string;
  browser?: string;
  os?: string;
  ip?: string;
  location?: string;
}

// Types for subscription
interface SubscriptionDetails {
  service: 'google' | 'apple' | 'stripe';
  productId?: string;
  startTimeMillis: number;
  expiryTimeMillis: number;
  priceCurrencyCode: string;
  countryCode: string;
  isCancelled: boolean;
  environment?: string;
}

interface SubscriptionResponse {
  subscription: SubscriptionDetails;
  isActive: boolean;
  daysRemaining: number;
}

function Dashboard(): JSX.Element {
  const { user, accessToken } = useUser();
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Subscription state
  const [subscriptionData, setSubscriptionData] =
    useState<SubscriptionResponse | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState<boolean>(true);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(
    null
  );

  // User accounts and invite codes counts
  const [accountsCount, setAccountsCount] = useState<number>(0);
  const [inviteCodesCount, setInviteCodesCount] = useState<number>(0);
  const [countsLoading, setCountsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Fetch active devices
    fetchActiveDevices();
    // Fetch subscription info
    fetchSubscriptionInfo();
    // Fetch user accounts and invite codes counts
    fetchUserCounts();
  }, [currentUser, accessToken]);

  const fetchActiveDevices = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDevicesCollectionRef = collection(userDocRef, 'devices');

      const devicesSnapshot = await getDocs(userDevicesCollectionRef);
      const devicesList: Device[] = [];

      devicesSnapshot.forEach((deviceDoc) => {
        const deviceData = deviceDoc.data() as Omit<Device, 'id'>;
        devicesList.push({
          id: deviceDoc.id,
          name: deviceData.name || 'Unknown Device',
          lastActive: deviceData.lastActive || 'Unknown',
          browser: deviceData.browser,
          os: deviceData.os,
          ip: deviceData.ip,
          location: deviceData.location,
        });
      });

      setDevices(devicesList);
    } catch (err) {
      console.error('Error fetching devices:', err);
      setError('Failed to fetch active devices');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCounts = async () => {
    if (!currentUser) return;

    try {
      setCountsLoading(true);
      const userDocRef = doc(db, 'users', currentUser.uid);

      // Get subaccounts count
      const subaccountsCollectionRef = collection(userDocRef, 'subaccounts');
      const subaccountsSnapshot = await getCountFromServer(
        subaccountsCollectionRef
      );
      setAccountsCount(subaccountsSnapshot.data().count);

      // Get invite codes count
      const inviteCodesCollectionRef = collection(userDocRef, 'inviteCodes');
      const inviteCodesSnapshot = await getCountFromServer(
        inviteCodesCollectionRef
      );
      setInviteCodesCount(inviteCodesSnapshot.data().count);
    } catch (err) {
      console.error('Error fetching user counts:', err);
    } finally {
      setCountsLoading(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      setSubscriptionLoading(true);
      setSubscriptionError(null);

      if (!accessToken) {
        setSubscriptionError('Authentication required');
        setSubscriptionLoading(false);
        return;
      }

      const response = await axios.get(`${FIREBASE_API_URL}/api/subscription`, {
        params: { accessToken: accessToken },
      });

      setSubscriptionData(response.data);
    } catch (err) {
      console.error('Error fetching subscription info:', err);
      setSubscriptionError('Failed to load subscription information');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const actionButtonStyles = {
    '& .material-icons-round': {
      transform: `translateX(0)`,
      transition: 'all 200ms cubic-bezier(0.34,1.61,0.7,1.3)',
    },

    '&:hover .material-icons-round, &:focus .material-icons-round': {
      transform: `translateX(4px)`,
    },
  };

  const getDeviceIcon = (device: Device) => {
    const os = device.os?.toLowerCase() || '';

    if (os.includes('iphone') || os.includes('ios')) {
      return 'phone_iphone';
    } else if (os.includes('android')) {
      return 'smartphone';
    } else {
      return 'desktop_windows';
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={3}>
              <DefaultStatisticsCard
                title="Balance "
                count={
                  !user
                    ? 'Loading...'
                    : `${user?.credits?.toFixed(2) || 0} credits`
                }
                percentage={{
                  color: 'success',
                  value: '',
                  label: '',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DefaultStatisticsCard
                title="Subscription price"
                count={!user ? 'Loading...' : `${PRICE_PER_TOKEN} tokens`}
                percentage={{
                  color: 'success',
                  value: `1 token ~ 1 USD`,
                  label: '',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DefaultStatisticsCard
                title="Accounts"
                count={countsLoading ? 'Loading...' : accountsCount.toString()}
                percentage={{
                  color: 'success',
                  value: '',
                  label: '',
                }}
              />
            </Grid>
            {/* <Grid item xs={12} sm={3}>
              <DefaultStatisticsCard
                title="Invite codes"
                count={
                  countsLoading ? 'Loading...' : inviteCodesCount.toString()
                }
                percentage={{
                  color: 'secondary',
                  value: '',
                  label: '',
                }}
              />
            </Grid> */}
          </Grid>
        </MDBox>

        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h5">
                    Subscription Information
                  </MDTypography>
                </MDBox>

                {subscriptionError && (
                  <MDBox px={3} mb={2}>
                    <MDAlert color="error">{subscriptionError}</MDAlert>
                  </MDBox>
                )}

                <MDBox component="form" pb={3} px={3}>
                  {subscriptionLoading ? (
                    <MDTypography variant="body2">
                      Loading subscription details...
                    </MDTypography>
                  ) : subscriptionData ? (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <MDTypography variant="button" fontWeight="bold">
                            Status
                          </MDTypography>
                          <MDTypography
                            variant="body2"
                            color={
                              subscriptionData.isActive ? 'success' : 'error'
                            }
                          >
                            {subscriptionData.isActive ? 'Active' : 'Inactive'}
                          </MDTypography>
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <MDTypography variant="button" fontWeight="bold">
                            Service Provider
                          </MDTypography>
                          <MDTypography
                            variant="body2"
                            textTransform="capitalize"
                          >
                            {subscriptionData.subscription.service}
                          </MDTypography>
                        </MDBox>
                      </Grid>

                      {subscriptionData.isActive && (
                        <Grid item xs={12} md={4}>
                          <MDBox mb={2}>
                            <MDTypography variant="button" fontWeight="bold">
                              Days Remaining
                            </MDTypography>
                            <MDTypography variant="body2">
                              {subscriptionData.daysRemaining}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      )}

                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <MDTypography variant="button" fontWeight="bold">
                            Start Date
                          </MDTypography>
                          <MDTypography variant="body2">
                            {formatDate(
                              subscriptionData.subscription.startTimeMillis
                            )}
                          </MDTypography>
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <MDTypography variant="button" fontWeight="bold">
                            Expiry Date
                          </MDTypography>
                          <MDTypography variant="body2">
                            {formatDate(
                              subscriptionData.subscription.expiryTimeMillis
                            )}
                          </MDTypography>
                        </MDBox>
                      </Grid>

                      {subscriptionData.subscription.productId && (
                        <Grid item xs={12} md={4}>
                          <MDBox mb={2}>
                            <MDTypography variant="button" fontWeight="bold">
                              Product ID
                            </MDTypography>
                            <MDTypography variant="body2">
                              {subscriptionData.subscription.productId}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      )}

                      {subscriptionData.subscription.isCancelled && (
                        <Grid item xs={12}>
                          <MDAlert color="warning">
                            This subscription has been cancelled.
                          </MDAlert>
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <MDTypography variant="body2">
                      No subscription information found.
                    </MDTypography>
                  )}

                  <MDBox mt={3} />

                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-end"
                    flexWrap="wrap"
                  >
                    <MDBox ml="auto">
                      <MDButton
                        variant="gradient"
                        color="info"
                        size="small"
                        disabled={subscriptionLoading}
                        component={Link}
                        to="/account/settings"
                      >
                        Manage Subscription
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        {/* <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12} lg={12}>
              <DefaultLineChart
                title="Accounts Graph"
                description={
                  <MDBox display="flex" justifyContent="space-between">
                    <MDBox display="flex" ml={-1}>
                      <MDBadgeDot
                        color="info"
                        size="sm"
                        badgeContent="Total accounts"
                      />
                      <MDBadgeDot
                        color="dark"
                        size="sm"
                        badgeContent="Active accounts"
                      />
                    </MDBox>
                    <MDBox mt={-4} mr={-1} position="absolute" right="1.5rem">
                      <Tooltip
                        title="See which ads perform better"
                        placement="left"
                        arrow
                      >
                        <MDButton
                          variant="outlined"
                          color="secondary"
                          size="small"
                          circular
                          iconOnly
                        >
                          <Icon>priority_high</Icon>
                        </MDButton>
                      </Tooltip>
                    </MDBox>
                  </MDBox>
                }
                chart={defaultLineChartData}
              />
            </Grid>
          </Grid>
        </MDBox> */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox pt={3} px={3}>
                <MDTypography variant="h6" fontWeight="medium">
                  My active devices
                </MDTypography>
              </MDBox>
              <MDBox pb={3} px={3} sx={{ overflow: 'auto' }}>
                {loading ? (
                  <MDTypography variant="button" color="text">
                    Loading devices...
                  </MDTypography>
                ) : devices.length > 0 ? (
                  devices.map((device, index) => (
                    <MDBox key={device.id}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width={{ xs: 'max-content', sm: '100%' }}
                        py={1}
                      >
                        <MDBox display="flex" alignItems="center">
                          <MDBox
                            textAlign="center"
                            color="text"
                            px={{ xs: 0, md: 1.5 }}
                            opacity={0.6}
                          >
                            <Icon>{getDeviceIcon(device)}</Icon>
                          </MDBox>
                          <MDBox height="100%" ml={2} lineHeight={1} mr={2}>
                            <MDTypography
                              display="block"
                              variant="button"
                              fontWeight="regular"
                              color="text"
                            >
                              {device.name} {device.ip && `(${device.ip})`}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              Last active: {device.lastActive}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                        <MDBox display="flex" alignItems="center">
                          <MDBadge
                            variant="contained"
                            size="xs"
                            badgeContent="active"
                            color="success"
                            container
                          />
                          {device.location && (
                            <MDBox mx={2} lineHeight={1}>
                              <MDTypography
                                variant="button"
                                color="secondary"
                                fontWeight="regular"
                              >
                                {device.location}
                              </MDTypography>
                            </MDBox>
                          )}
                          <MDTypography
                            component="a"
                            href="#"
                            variant="button"
                            color="info"
                            fontWeight="regular"
                            sx={actionButtonStyles}
                          >
                            See more&nbsp;
                            <Icon
                              sx={{
                                fontWeight: 'bold',
                                verticalAlign: 'middle',
                              }}
                            >
                              arrow_forward
                            </Icon>
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      {index < devices.length - 1 && <Divider />}
                    </MDBox>
                  ))
                ) : (
                  <MDTypography variant="button" color="text">
                    No active devices found
                  </MDTypography>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
